import React, { useRef } from "react";

const KNOB_ANGLE_MIN = -135;
const KNOB_ANGLE_MAX = 135;
const KNOB_ANGLE_SPAN = KNOB_ANGLE_MAX - KNOB_ANGLE_MIN;

let uid = 0;
const nextId = () => ++uid;

export default function Knob({
  value,
  onChange,
  size = 144,
  defaultValue = 0.5,
  colorLow = "#60a5fa",
  colorHigh = "#f472b6",
  glowLow = "#3b82f6",
  glowHigh = "#ec4899",
  ariaLabel = "Knob",
  disabled = false,
  wheelStep = 0.02,
  wheelStepFine = 0.002,
}) {
  const knobRef = useRef(null);
  const dragRef = useRef(null);
  const idRef = useRef(null);
  if (idRef.current == null) idRef.current = nextId();
  const gid = idRef.current;

  const cx = size / 2;
  const cy = size / 2;
  const angle = KNOB_ANGLE_MIN + value * KNOB_ANGLE_SPAN;
  const originAngle = KNOB_ANGLE_MIN + defaultValue * KNOB_ANGLE_SPAN;

  const polar = (r, deg) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };

  const pointerAngle = (clientX, clientY) => {
    const rect = knobRef.current.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    return (Math.atan2(dx, -dy) * 180) / Math.PI;
  };

  const onPointerDown = (e) => {
    if (disabled) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    knobRef.current?.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      startAngle: pointerAngle(e.clientX, e.clientY),
      startValue: value,
      pointerId: e.pointerId,
    };
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    let delta = pointerAngle(e.clientX, e.clientY) - dragRef.current.startAngle;
    while (delta > 180) delta -= 360;
    while (delta < -180) delta += 360;
    const next = dragRef.current.startValue + delta / KNOB_ANGLE_SPAN;
    onChange(Math.max(0, Math.min(1, next)));
  };

  const endDrag = (e) => {
    if (!dragRef.current) return;
    knobRef.current?.releasePointerCapture?.(dragRef.current.pointerId);
    dragRef.current = null;
    if (e?.preventDefault) e.preventDefault();
  };

  const onWheel = (e) => {
    if (disabled) return;
    e.preventDefault();
    const step = e.shiftKey ? wheelStepFine : wheelStep;
    const next = value + (e.deltaY > 0 ? step : -step);
    onChange(Math.max(0, Math.min(1, next)));
  };

  const onDoubleClick = () => {
    if (disabled) return;
    onChange(defaultValue);
  };

  const outerR = size / 2 - 1;
  const ringR = outerR - Math.max(2, size * 0.02);
  const trackR = ringR - Math.max(2, size * 0.02);
  const knobR = trackR - Math.max(6, size * 0.06);
  const indicatorOuter = knobR - Math.max(2, size * 0.02);
  const indicatorInner = knobR - Math.max(12, size * 0.12);
  const tickInner = trackR - 1;
  const tickOuter = ringR + 1;

  const ticks = [];
  for (let i = 0; i <= 12; i++) {
    const tDeg = KNOB_ANGLE_MIN + (i / 12) * KNOB_ANGLE_SPAN;
    const major = i % 2 === 0;
    const innerR = major ? tickInner - 2 : tickInner;
    const [x1, y1] = polar(innerR, tDeg);
    const [x2, y2] = polar(tickOuter, tDeg);
    ticks.push({ x1, y1, x2, y2, major, key: i });
  }

  const arcDelta = angle - originAngle;
  const showArc = Math.abs(arcDelta) > 0.5;
  const [arcX1, arcY1] = polar(trackR, originAngle);
  const [arcX2, arcY2] = polar(trackR, angle);
  const arcSweep = arcDelta > 0 ? 1 : 0;
  const arcLargeArc = Math.abs(arcDelta) > 180 ? 1 : 0;
  const arcPath = showArc
    ? `M ${arcX1} ${arcY1} A ${trackR} ${trackR} 0 ${arcLargeArc} ${arcSweep} ${arcX2} ${arcY2}`
    : "";
  const arcColor = value < defaultValue ? colorLow : colorHigh;
  const arcGlow = value < defaultValue ? glowLow : glowHigh;

  const [indX1, indY1] = polar(indicatorInner, 0);
  const [indX2, indY2] = polar(indicatorOuter, 0);
  const [origX1, origY1] = polar(tickInner - 3, originAngle);
  const [origX2, origY2] = polar(tickOuter + 2, originAngle);

  const strokeIndicator = Math.max(3, size * 0.035);
  const strokeIndicatorInner = Math.max(1.75, size * 0.018);

  return (
    <div
      ref={knobRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      onDoubleClick={onDoubleClick}
      onWheel={onWheel}
      role="slider"
      aria-label={ariaLabel}
      aria-disabled={disabled}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      tabIndex={disabled ? -1 : 0}
      className={`relative select-none touch-none ${disabled ? "cursor-not-allowed opacity-50" : "cursor-grab active:cursor-grabbing"} focus:outline-none focus:ring-2 focus:ring-amber-400/40 rounded-full`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="block">
        <defs>
          <radialGradient id={`knob-bezel-${gid}`} cx="50%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#3a3a3a" />
            <stop offset="55%" stopColor="#1a1a1a" />
            <stop offset="100%" stopColor="#050505" />
          </radialGradient>
          <linearGradient id={`knob-ring-${gid}`} x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#5a5a5a" />
            <stop offset="50%" stopColor="#262626" />
            <stop offset="100%" stopColor="#0a0a0a" />
          </linearGradient>
          <radialGradient id={`knob-body-${gid}`} cx="50%" cy="28%" r="75%">
            <stop offset="0%" stopColor="#2c2c2c" />
            <stop offset="55%" stopColor="#0e0e0e" />
            <stop offset="100%" stopColor="#000" />
          </radialGradient>
          <radialGradient id={`knob-cap-${gid}`} cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#262626" />
            <stop offset="100%" stopColor="#050505" />
          </radialGradient>
          <filter id={`knob-shadow-${gid}`} x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.7" />
          </filter>
        </defs>

        <circle cx={cx} cy={cy} r={outerR} fill={`url(#knob-ring-${gid})`} />
        <circle cx={cx} cy={cy} r={ringR} fill={`url(#knob-bezel-${gid})`} />

        {ticks.map((t) => (
          <line
            key={t.key}
            x1={t.x1}
            y1={t.y1}
            x2={t.x2}
            y2={t.y2}
            stroke={t.major ? "#737373" : "#3f3f3f"}
            strokeWidth={t.major ? 1.5 : 1}
            strokeLinecap="round"
          />
        ))}

        <line
          x1={origX1}
          y1={origY1}
          x2={origX2}
          y2={origY2}
          stroke="#fbbf24"
          strokeWidth={1.75}
          strokeLinecap="round"
          opacity="0.85"
          style={{ filter: "drop-shadow(0 0 2px rgba(251, 191, 36, 0.7))" }}
        />

        {arcPath && (
          <path
            d={arcPath}
            stroke={arcColor}
            strokeWidth={3}
            strokeLinecap="round"
            fill="none"
            style={{ filter: `drop-shadow(0 0 5px ${arcGlow})` }}
          />
        )}

        <circle cx={cx} cy={cy + 1} r={knobR + 1} fill="#000" opacity="0.55" />

        <circle
          cx={cx}
          cy={cy}
          r={knobR}
          fill={`url(#knob-body-${gid})`}
          stroke="#1f1f1f"
          strokeWidth="1"
          filter={`url(#knob-shadow-${gid})`}
        />

        <ellipse
          cx={cx}
          cy={cy - knobR * 0.32}
          rx={knobR * 0.55}
          ry={knobR * 0.18}
          fill="#ffffff"
          opacity="0.07"
        />

        <g transform={`rotate(${angle} ${cx} ${cy})`}>
          <line
            x1={indX1}
            y1={indY1}
            x2={indX2}
            y2={indY2}
            stroke="#0a0a0a"
            strokeWidth={strokeIndicator}
            strokeLinecap="round"
            opacity="0.8"
          />
          <line
            x1={indX1}
            y1={indY1}
            x2={indX2}
            y2={indY2}
            stroke="#ffffff"
            strokeWidth={strokeIndicatorInner}
            strokeLinecap="round"
            style={{ filter: "drop-shadow(0 0 3px rgba(255,255,255,0.55))" }}
          />
        </g>

        <circle
          cx={cx}
          cy={cy}
          r={knobR * 0.22}
          fill={`url(#knob-cap-${gid})`}
          stroke="#2a2a2a"
          strokeWidth="1"
        />
        <circle cx={cx} cy={cy - knobR * 0.05} r={knobR * 0.08} fill="#fff" opacity="0.06" />
      </svg>
    </div>
  );
}
